import React from 'react';
import { Box, Typography, IconButton, Tooltip, useTheme, Stack } from '@mui/material';
import { Sync as SyncIcon, Warning as WarningIcon, Error as ErrorIcon } from '@mui/icons-material';
import filterSyncErrors from './filterSyncErrors';

type Props = {
  title: string;
  date: string;
  status: string;
  refreshDate: (title: string) => void;
  isDisabled?: boolean; // Optional, um Abwärtskompatibilität zu gewährleisten
  disabledTooltip?: string; // Tooltip-Text für den deaktivierten Zustand
};

type SyncLogEntry = { message: string; severity: string; location: string };

function getSeverityColor(logs?: SyncLogEntry[]) {
  if (!logs || logs.length === 0) {
    return 'green'; // Kein Array vorhanden → Grün
  }

  const severities = logs.map((log) => log.severity);

  if (severities.includes('error')) {
    return 'red'; // Mindestens ein Fehler → Rot
  }

  if (severities.includes('warning')) {
    return 'orange'; // Mindestens eine Warnung → Orange
  }

  return 'green'; // Falls keine bekannten Severity-Werte vorhanden sind
}

function ItemProject({ title, date, status, refreshDate, isDisabled = false, disabledTooltip = '' }: Props) {
  const theme = useTheme();
  const PRIMARY_LIGHT = theme.palette.grey[700];
  const PRIMARY_DARK = theme.palette.grey[400];

  const dateObj = new Date(date);
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const year = dateObj.getFullYear();

  const projectStatusRaw = JSON.parse(status);
  const projectStatus = filterSyncErrors(projectStatusRaw);

  const iconColor = React.useMemo(() => {
    if (isDisabled) {
      return 'rgba(0, 0, 0, 0.35)';
    }
    return theme.palette.mode === 'dark' ? PRIMARY_DARK : PRIMARY_LIGHT;
  }, [isDisabled, theme.palette.mode, PRIMARY_DARK, PRIMARY_LIGHT]);

  return (
    <Stack direction="column">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography component="h3" fontSize="1.2em">
          {title}
        </Typography>
        <Tooltip title={isDisabled ? disabledTooltip : `Berater mit ${title} synchronisieren`} arrow>
          <span>
            {' '}
            {/* Wrapper für IconButton im deaktivierten Zustand */}
            <IconButton
              size="small"
              onClick={() => {
                refreshDate(title);
              }}
              component="span"
              disabled={isDisabled}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.6,
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '50%',
                },
              }}
            >
              <SyncIcon
                sx={{
                  fontSize: '30px',
                  color: iconColor,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack direction="row" justifyContent="space-between">
        <Stack spacing={1} width="100%" direction="column">
          <Stack direction="row" justifyContent="space-between">
            <Typography component="span" fontSize="0.8em">
              letztes Update:
            </Typography>
            <Typography component="span" fontSize="0.8em">
              {`${day}.${month}.${year}`}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography component="span" fontSize="0.8em">
              Status:
            </Typography>
            <Box
              sx={{
                width: '20px',
                height: '20px',
                background: getSeverityColor(projectStatus),
                borderRadius: '50%',
              }}
            />
          </Stack>

          {projectStatus && projectStatus.length > 0 && (
            <Stack direction="column" justifyContent="space-between">
              {projectStatus.map((log: any, index: number) => (
                <Stack
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${log.severity}-${log.message}-${index}`}
                  direction="row"
                  alignItems="flex-start"
                  spacing={1}
                >
                  {log.severity === 'error' ? (
                    <ErrorIcon color="error" fontSize="small" />
                  ) : (
                    <WarningIcon sx={{ color: 'orange' }} fontSize="small" />
                  )}
                  <Typography
                    component="span"
                    fontSize="0.8em"
                    sx={{
                      wordBreak: 'break-word', // Bricht lange Wörter um
                      overflowWrap: 'break-word', // Sorgt für besseres Umbruchsverhalten
                      display: 'block', // Zwingt den Text in eine neue Zeile, falls nötig
                      maxWidth: '100%', // Damit sich der Text an das Layout anpasst
                    }}
                  >
                    {log.message}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

export default ItemProject;
